import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { LinkButton } from '~/features/navigation'
import { ScorecardMutators } from '~/mutators/scorecards'
import { useTRPC } from '~/services/trpc'

import {
  AlertDialog,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Heading1,
} from '@hap/components'
import { DuplicateScorecardForm } from '../../settings/scorecards/-components/scorecard-forms'

export const Route = createFileRoute('/_authenticated/$alias/_app/scorecards/')({
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const navigate = Route.useNavigate()
  const params = Route.useParams()
  const alias = params.alias

  const { data: scorecards } = useQuery(trpc.scorecards.getAll.queryOptions())

  // Dialog state
  const [duplicateVisibility, setDuplicateVisibility] = useState(false)
  const [deleteVisibility, setDeleteVisibility] = useState(false)
  const [activeScorecardId, setActiveScorecardId] = useState<string | null>(null)
  const [activeScorecardName, setActiveScorecardName] = useState('')

  const { destroy } = ScorecardMutators.useDestroy({
    onSuccess: () => {
      setDeleteVisibility(false)
      setActiveScorecardId(null)
    },
  })

  const handleDelete = useCallback(async () => {
    if (activeScorecardId) {
      await destroy({ scorecardId: activeScorecardId })
    }
  }, [activeScorecardId, destroy])

  const openDuplicate = (scorecardId: string, scorecardName: string) => {
    setActiveScorecardId(scorecardId)
    setActiveScorecardName(scorecardName)
    setDuplicateVisibility(true)
  }

  const openDelete = (scorecardId: string) => {
    setActiveScorecardId(scorecardId)
    setDeleteVisibility(true)
  }

  return (
    <div className="space-y-4 p-4">
      <header className="flex items-center justify-between">
        <Heading1>Scorecards</Heading1>
        <LinkButton to="/$alias/scorecard/new" from={Route.fullPath} variant="lime">
          New Scorecard
        </LinkButton>
      </header>

      <div className="space-y-4">
        {scorecards?.map((scorecard) => {
          const isArchived = !!scorecard.archivedAt
          const isOrgWide = !scorecard.workspace

          return (
            <div
              key={scorecard.id}
              className="bg-accent flex items-center justify-between rounded-md p-4"
            >
              <div className="flex items-center gap-4">
                <Link
                  to="../scorecard/$scorecardId"
                  from={Route.fullPath}
                  params={{ scorecardId: scorecard.id }}
                  className="text-lg font-semibold"
                >
                  {scorecard.name}
                </Link>
                {isArchived && <Badge variant="outline">Archived</Badge>}
                <Badge variant={isOrgWide ? 'secondary' : 'outline'}>
                  {isOrgWide ? 'Org-wide' : scorecard.workspace?.name}
                </Badge>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" icon="dots-three-outline" size="icon" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({
                        to: '/$alias/scorecard/$scorecardId/edit',
                        params: { alias, scorecardId: scorecard.id },
                      })
                    }
                    icon="gear-six"
                  >
                    Configure
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => openDuplicate(scorecard.id, scorecard.name)}
                    icon="copy"
                  >
                    Duplicate...
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => openDelete(scorecard.id)}
                    icon="trash"
                    variant="destructive"
                  >
                    Delete...
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        })}
      </div>

      <DuplicateScorecardForm
        open={duplicateVisibility}
        onOpenChange={setDuplicateVisibility}
        scorecardId={activeScorecardId || ''}
        scorecardName={activeScorecardName}
        onSuccess={(newScorecardId: string) => {
          navigate({
            to: '/$alias/scorecard/$scorecardId',
            params: { alias, scorecardId: newScorecardId },
          })
        }}
      />

      <AlertDialog
        open={deleteVisibility}
        onOpenChange={setDeleteVisibility}
        title="Delete scorecard"
        description="Confirm that you wish to delete this scorecard. This cannot be undone."
        action={{
          onClick: handleDelete,
          value: 'Delete',
          variant: 'destructive',
        }}
      />
    </div>
  )
}
